import data from "@emoji-mart/data";
import Picker from "@emoji-mart/react";
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useFloating,
} from "@floating-ui/react";
import { FaceHappy } from "@untitled-ui/icons-react";
import { useState } from "react";

import { useOnClickOutside } from "@/hooks";
import { locale } from "@/lib/i18n";
import { cn } from "@/lib/utils";

import "./emojipicker.css";

type Emoji = {
  id: string;
  keywords: string[];
  name: string;
  native: string;
  shortcodes: string;
  unified: string;
};

type Props = {
  value?: Emoji["native"];
  onChange?: (emoji: Emoji["native"]) => void;
  keepOpen?: boolean;
  buttonClassName?: string;
  iconClassName?: string;
};

export default function EmojiPicker({
  value,
  onChange,
  keepOpen = false,
  buttonClassName = "",
  iconClassName = "size-5",
}: Props) {
  const [open, setOpen] = useState(false);

  const {
    refs: { setReference, setFloating, reference, floating },
    floatingStyles,
  } = useFloating({
    open,
    middleware: [
      offset(8),
      flip({
        fallbackAxisSideDirection: "end",
      }),
      shift({
        padding: 8,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  useOnClickOutside([reference, floating], () => {
    setOpen(false);
  });

  return (
    <>
      <button
        ref={setReference}
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          "flex size-11 cursor-pointer items-center justify-center rounded-lg text-gray-600 transition duration-200 ease-in-out hover:bg-gray-50 hover:text-gray-700 focus:outline-none",
          buttonClassName,
        )}
      >
        {value ? (
          <span className="text-xl leading-none">{value}</span>
        ) : (
          <FaceHappy className={iconClassName} />
        )}
      </button>

      {open && (
        <FloatingPortal>
          <div
            ref={setFloating}
            style={floatingStyles}
            className="z-9999 overflow-hidden rounded-xl border border-gray-200 bg-white p-0 shadow-xl"
          >
            <Picker
              key={locale.key}
              theme="light"
              data={data}
              set="native"
              onEmojiSelect={(emoji: Emoji) => {
                onChange?.(emoji.native);

                if (!keepOpen) {
                  setOpen(false);
                }
              }}
              locale={locale.key}
              previewPosition="none"
            />
          </div>
        </FloatingPortal>
      )}
    </>
  );
}
